import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  ChevronDown, ChevronUp, HelpCircle, Loader2,
  ArrowRightLeft, Leaf, TrendingUp, AlertTriangle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { fallanalyseAPI } from '@/lib/api';

const TYP_ICONS = {
  zeitverschiebung: Clock,
  zielgewichtung_wirtschaftlich: TrendingUp,
  zielgewichtung_oekologisch: Leaf,
  stolperstein_eintritt: AlertTriangle,
  massnahme_alternative: ArrowRightLeft,
};

const ERGEBNIS_LABELS = (t) => ({
  was_aendert_sich: t('szenario.wasAendertSich'),
  was_wird_kritischer_oder_entspannter: t('szenario.wasWirdKritischer'),
  welche_offenen_punkte_verschieben_sich: t('szenario.welcheOffenenPunkte'),
  was_waere_jetzt_sinnvoll: t('szenario.wasWaereSinnvoll'),
});

function SzenarioErgebnis({ ergebnis, t }) {
  const labels = ERGEBNIS_LABELS(t);
  const bloecke = [
    { key: 'was_aendert_sich', cls: 'border-blue-200 bg-blue-50/40' },
    { key: 'was_wird_kritischer_oder_entspannter', cls: 'border-amber-200 bg-amber-50/40' },
    { key: 'welche_offenen_punkte_verschieben_sich', cls: 'border-slate-200 bg-slate-50/40' },
    { key: 'was_waere_jetzt_sinnvoll', cls: 'border-emerald-200 bg-emerald-50/40' },
  ];

  return (
    <div className="space-y-3 mt-4" data-testid="szenario-ergebnis">
      {bloecke.map(({ key, cls }) => (
        <div key={key} className={`p-3.5 rounded-lg border ${cls}`}>
          <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wide mb-1.5">
            {labels[key]}
          </p>
          <p className="text-sm text-foreground leading-relaxed">
            {ergebnis[key]}
          </p>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
        {t('szenario.disclaimer')}
      </p>
    </div>
  );
}

export function SzenarioView({ entwurfId, stufeCStatus, t, sprache }) {
  const [open, setOpen] = useState(false);
  const [optionen, setOptionen] = useState([]);
  const [ladenOptionen, setLadenOptionen] = useState(false);
  const [aktiverTyp, setAktiverTyp] = useState(null);
  const [ladenErgebnis, setLadenErgebnis] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  const istFreigeschaltet = stufeCStatus === 'freigeschaltet';

  const ladeOptionen = useCallback(async () => {
    if (!istFreigeschaltet || optionen.length > 0) return;
    setLadenOptionen(true);
    try {
      const res = await fallanalyseAPI.szenarioOptionen(entwurfId, sprache || 'de');
      setOptionen(res.data.optionen || []);
    } catch {
      toast.error(t('szenario.fehlerLaden'));
    } finally {
      setLadenOptionen(false);
    }
  }, [entwurfId, sprache, istFreigeschaltet, optionen.length, t]);

  useEffect(() => {
    if (open && optionen.length === 0 && istFreigeschaltet) {
      ladeOptionen();
    }
  }, [open, ladeOptionen, optionen.length, istFreigeschaltet]);

  const handleSzenarioKlick = async (typ) => {
    setAktiverTyp(typ);
    setErgebnis(null);
    setLadenErgebnis(true);
    try {
      const res = await fallanalyseAPI.szenarioAusfuehren(entwurfId, typ, sprache || 'de');
      if (res.data.status === 'ok') {
        setErgebnis(res.data.ergebnis);
      } else {
        toast.error(res.data.hinweis || t('szenario.fehlerAuswertung'));
        setAktiverTyp(null);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || t('szenario.fehlerAuswertung');
      toast.error(msg);
      setAktiverTyp(null);
    } finally {
      setLadenErgebnis(false);
    }
  };

  // Stufe B: gesperrter Hinweis
  if (!istFreigeschaltet) {
    return (
      <div className="mt-4 p-4 rounded-lg border border-warm-200 bg-warm-50/30" data-testid="szenario-gesperrt">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <HelpCircle className="h-4 w-4 shrink-0" />
          <span>{t('szenario.gesperrtHinweis')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5" data-testid="szenario-bereich">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
        data-testid="szenario-toggle"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <HelpCircle className="h-4 w-4 text-primary" />
        {t('szenario.titel')}
      </button>

      {open && (
        <div className="mt-4 space-y-3" data-testid="szenario-optionen-liste">
          {ladenOptionen ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('szenario.ladenOptionen')}
            </div>
          ) : optionen.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3">{t('szenario.keineOptionen')}</p>
          ) : (
            optionen.map((opt) => {
              const Icon = TYP_ICONS[opt.szenario_typ] || HelpCircle;
              const istAktiv = aktiverTyp === opt.szenario_typ;
              return (
                <div key={opt.szenario_typ} data-testid={`szenario-karte-${opt.szenario_typ}`}>
                  <button
                    onClick={() => handleSzenarioKlick(opt.szenario_typ)}
                    disabled={ladenErgebnis}
                    className={`w-full text-left p-3.5 rounded-lg border transition-all ${
                      istAktiv
                        ? 'border-primary/30 bg-primary/[0.04] shadow-sm'
                        : 'border-warm-200 bg-warm-50/20 hover:border-primary/20 hover:bg-primary/[0.02]'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {ladenErgebnis && istAktiv ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                      ) : (
                        <Icon className={`h-4 w-4 shrink-0 ${istAktiv ? 'text-primary' : 'text-muted-foreground'}`} />
                      )}
                      <span className={`text-sm leading-relaxed ${istAktiv ? 'font-medium text-foreground' : 'text-foreground/80'}`}>
                        {opt.frage}
                      </span>
                    </div>
                    {opt.kontext && (
                      <p className="text-[11px] text-muted-foreground mt-1.5 ml-6.5">
                        {t('szenario.bezugAuf')}: {opt.kontext}
                      </p>
                    )}
                  </button>
                  {istAktiv && ergebnis && (
                    <SzenarioErgebnis ergebnis={ergebnis} t={t} />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
